import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';

const validators = {
  valueRequired(message = '字段必填') {
    return (val: any) => {
      if (val instanceof Date) return '';
      if (typeof val === 'boolean' || typeof val === 'number') return '';
      if (typeof val === 'string' && !val.trim()) return message;
      if (typeof val === 'undefined') return message;
      if (typeof val === 'object') {
        if (isEmpty(val)) {
          return message;
        } else if (values(val).filter(Boolean).length === 0) {
          return message;
        }
      }
      return '';
    };
  },
  numberRange(
    min: number,
    max: number,
    fractionDigits = 0,
    message = `请输入${min}-${max}的数字${
      fractionDigits ? `，并保留${fractionDigits}位小数` : ''
    }`,
  ) {
    return (val: any) => {
      if (val == null || val === '') return '';
      try {
        const regex = new RegExp(
          '^\\d+' +
            (fractionDigits ? '(\\.\\d{1,' + fractionDigits + '})?' : '') +
            '$',
        );
        const regexZero = /^(0|[1-9]\d*)(\.\d+)?$/;
        return regex.test(val) &&
          regexZero.test(val) &&
          parseFloat(val) >= min &&
          parseFloat(val) <= max
          ? ''
          : message;
      } catch {
        return message;
      }
    };
  },
  stringRangeLength(
    min: number,
    max: number,
    message = `请输入${min}-${max}个字数`,
  ) {
    return (val?: string) => {
      const length = val?.length ?? 0;
      if (length < min || length > max) {
        return message;
      }
      return '';
    };
  },
  phoneNumber(message = '请输入正确格式的手机号码') {
    return (val?: string) => {
      if (!val) return '';
      if (!/^1[3-9]\d{9}$/.test(val)) {
        return message;
      }
      return '';
    };
  },
  email(message = '请输入正确的邮箱') {
    return (val?: string) => {
      if (!val) return '';
      const vVal = val.trim();
      if (!/^\w+[-.\w]+@\w+\.\w{2,3}/.test(vVal)) {
        return message;
      }
      if (!/^\w+[.-]?\w+@\w+[.-]?\w+(?:\.\w{2,3})+$/.test(vVal)) {
        return message;
      }
      return '';
    };
  },
  stringMaxLength(max: number, message = `请输入不超过${max}个字数的内容`) {
    return (val?: string) => {
      if (!val) return '';
      const vVal = val.trim();
      if (vVal.length > max) {
        return message;
      }
      return '';
    };
  },
  stringMinLength(min: number, message = `请输入不少于${min}个字数的内容`) {
    return (val?: string) => {
      if (!val) return '';
      const vVal = val.trim();
      if (vVal.length < min) {
        return message;
      }
      return '';
    };
  },
  richTextMaxLength(max: number, message = `请输入不超过${max}个字数的内容`) {
    return (val?: string) => {
      if (!val) return '';
      if (val.replace(/<[^>]+>/g, '').replace('\n', '').length > max) {
        return message;
      }
      return '';
    };
  },
  nameValidator(message = '请输入正确的姓名') {
    return (val?: string) => {
      if (!val) return '';
      const vVal = val.trim();
      if (
        /^[a-zA-Z\u4e00-\u9fa5]+(?:\s?[a-zA-Z\u4e00-\u9fa5])*$/.test(vVal) &&
        !/[\u4e00-\u9fa5]\s+[\u4e00-\u9fa5]/.test(vVal)
      ) {
        return '';
      }
      return message;
    };
  },
  companyNameValidator(message = '请输入正确的公司名称') {
    return (val?: string) => {
      if (!val) return '';
      const vVal = val.trim();
      if (
        !/^[\u4e00-\u9fa5a-zA-Z()（）]{2,50}(公司|集团|有限公司|股份有限公司)$/.test(
          vVal,
        )
      ) {
        return message;
      }
      return '';
    };
  },
  dateRangeValidator(message = '结束时间不能早于开始时间') {
    return (startDate: string, endDate: string) => {
      return moment(startDate).valueOf() > moment(endDate).valueOf()
        ? message
        : '';
    };
  },
  identityValidator(message = '请输入正确的身份证号码') {
    return (val?: string) => {
      if (!val) return '';
      if (
        !/^[1-9]\d{7}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}$/.test(val) &&
        !/^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}[0-9Xx]$/.test(
          val,
        )
      ) {
        return message;
      }
      return '';
    };
  },
  wechatValidator(message = '请输入正确的微信号') {
    return (val?: string) => {
      if (!val) return '';
      if (
        !/^[_a-zA-Z]([-_a-zA-Z0-9]{5,19})+$/.test(val) &&
        !/^1[3-9]\d{9}$/.test(val) &&
        !/^[1-9][0-9]{4,10}$/.test(val)
      ) {
        return message;
      }
      return '';
    };
  },
  websiteValidator(message = '请输入正确的网站地址') {
    return (val?: string) => {
      if (!val) return '';
      const protocol = '(?:(?:[a-z]+:)?//)';
      const auth = '(?:\\S+(?::\\S*)?@)?';
      const host =
        '(?:(?:[a-z\\u00a1-\\uffff0-9][-_]*)*[a-z\\u00a1-\\uffff0-9]+)';
      const domain =
        '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*';
      const tld = '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))\\.?';
      const port = '(?::\\d{2,5})?';
      const path = '(?:[/?#][^\\s"]*)?';
      const regex = `(?:${protocol}|www\\.)${auth}(?:${host}${domain}${tld})${port}${path}`;

      if (!new RegExp(regex, 'ig').test(val)) {
        return message;
      }
      return '';
    };
  },
  telValidator: (val: string) => {
    if (!/^1[3-9]\d{9}$/.test(val.replace(/\s/g, ''))) {
      return '请输入正确的手机号';
    }
    return '';
  },
  optValidator: (val: string) => {
    if (!/^\d{6}$/.test(val)) {
      return '请输入正确的验证码';
    }
    return '';
  },
};

export default validators;
